import { FC, Fragment } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useSignal } from "@preact/signals";
import { Link, useNavigate } from "react-router-dom";
import { Appearance, MatchResult } from "~/core";
import { useApp } from "~/core/store";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import InventoryIcon from "@material-design-icons/svg/outlined/inventory.svg";
import SearchIcon from "@material-design-icons/svg/outlined/search.svg";
import Loader from "~/assets/loader.svg";

const MAX_RESULTS = 8;

const Search: FC = () => {
    const navigate = useNavigate();
    const isFocus = useSignal(false);
    const isExpanded = useSignal(false);
    const isActive = useSignal(false);
    const query = useSignal("");
    const rootRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);
    const {
        search: { loading, results, search: searchFor },
        dialog: { issueTracker },
    } = useApp();

    useEffect(() => {
        const root = rootRef.current;

        if (!root) {
            return;
        }

        const onMouseDown = (event: MouseEvent) => {
            if (!isActive.peek() || root.contains(event.target as Node)) {
                return;
            }

            isActive.value = false;
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.code === "KeyF" && event.ctrlKey) {
                event.preventDefault();
                isExpanded.value = true;
                inputRef.current?.focus();
            } else if (event.code === "Escape" && isActive.value) {
                isActive.value = false;
                inputRef.current?.blur();
            }
        };

        window.addEventListener("mousedown", onMouseDown, true);
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("mousedown", onMouseDown, true);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isActive, isExpanded]);

    const getAppearance = (appearance: Appearance) => {
        if (typeof appearance === "object" && "text" in appearance) {
            const { icon: Icon, text } = appearance;

            if (!Icon) {
                return <span>{text}</span>;
            }

            return (
                <span className="inline-flex items-center">
                    <Icon
                        width="20"
                        height="20"
                        className="mr-2 min-w-[20px] fill-current"
                    />
                    <span>{text}</span>
                </span>
            );
        }

        return appearance;
    };

    const getLinkResult = ({ action, appearance }: MatchResult) => {
        if (!("link" in action)) {
            return null;
        }

        return (
            <Link
                className="block rounded-lg px-4 py-1 outline-none hover:text-sky-500 focus:bg-sky-100 focus:text-sky-500"
                to={action.link}
            >
                {getAppearance(appearance)}
            </Link>
        );
    };

    const getCallbackResult = ({ action, appearance }: MatchResult) => {
        if (!("callback" in action)) {
            return null;
        }

        return (
            <button
                type="button"
                onClick={action.callback}
                className="block w-full rounded-lg px-4 py-1 text-left outline-none hover:text-sky-500 focus:bg-sky-100 focus:text-sky-500"
            >
                {getAppearance(appearance)}
            </button>
        );
    };

    const clear = () => {
        isActive.value = false;
        inputRef.current?.blur();
        query.value = "";
        searchFor("");
    };

    const handleNavigate = (offset: number) => {
        if (!listRef.current || !offset || !rootRef.current) {
            return;
        }

        const root = rootRef.current;
        const list = listRef.current;
        const active = document.activeElement;
        const closestItem = active?.closest<HTMLElement>("[data-list-index]");

        if (active === inputRef.current) {
            list.querySelector<HTMLElement>("a,button")?.focus();
        } else if (closestItem) {
            let nextIndex = Number(closestItem.dataset.listIndex) + offset;

            if (nextIndex < 0) {
                nextIndex = results.value.length - 1;
            } else if (nextIndex >= results.value.length) {
                nextIndex = 0;
            }

            root
                .querySelector<HTMLElement>(
                    `[data-list-index="${nextIndex}"] button, [data-list-index="${nextIndex}"] a`,
                )
                ?.focus();
        }
    };

    return (
        <div
            ref={rootRef}
            className="absolute right-[360px] z-[60] hidden md:block"
            onKeyDown={(event) => {
                if (event.code === "ArrowUp") {
                    handleNavigate(-1);
                } else if (event.code === "ArrowDown") {
                    handleNavigate(1);
                }
            }}
        >
            <div className="relative">
                <div className="flex">
                    <Transition
                        show={isExpanded.value}
                        enter="transition ease-out duration-200 origin-right"
                        enterFrom="opacity-0 translate-x-2"
                        enterTo="opacity-100 translate-x-0"
                        leave="transition ease-in duration-150 origin-right"
                        leaveFrom="opacity-100 translate-x-0"
                        leaveTo="opacity-0 translate-x-2"
                        as={Fragment}
                        afterEnter={() => inputRef.current?.focus()}
                    >
                        <label className="flex w-[360px] items-center">
                            <input
                                ref={inputRef}
                                placeholder="Search App"
                                className="w-full rounded bg-white bg-opacity-80 px-4 py-1 outline-none focus:bg-opacity-100 focus:ring focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-sky-700"
                                type="text"
                                spellCheck={false}
                                name="search"
                                value={query.value}
                                autoComplete="off"
                                onInput={({ currentTarget }) => {
                                    query.value = currentTarget.value;
                                    searchFor(currentTarget.value);
                                }}
                                onKeyDown={(event) => {
                                    const [firstResult] = results.value;

                                    if (event.code === "Enter" && firstResult) {
                                        if ("link" in firstResult.action) {
                                            navigate(firstResult.action.link);
                                        } else {
                                            firstResult.action.callback();
                                        }
                                        clear();
                                    }
                                }}
                                onFocus={() =>
                                    (isFocus.value = isActive.value = true)
                                }
                                onBlur={(event) => {
                                    isFocus.value = false;

                                    if (
                                        event.relatedTarget &&
                                        !rootRef.current?.contains(
                                            event.relatedTarget as Node,
                                        )
                                    ) {
                                        isActive.value = false;
                                    }
                                }}
                            />
                        </label>
                    </Transition>
                    <button
                        type="button"
                        className="ml-4 flex items-center rounded-full fill-white p-2 outline-none transition duration-100 hover:bg-white/20 focus:ring focus:ring-white"
                        title="Search App"
                        onClick={() => (isExpanded.value = !isExpanded.value)}
                    >
                        {isExpanded.value ? (
                            <CloseIcon width="20" height="20" />
                        ) : (
                            <SearchIcon width="20" height="20" />
                        )}
                    </button>
                </div>
                <Transition
                    show={isActive.value && isExpanded.value}
                    enterFrom="opacity-0 translate-y-2"
                    enter="transition easy-in-out duration-200"
                    leave=""
                    leaveTo="opacity-0 translate-y-2"
                    className="overflow-y-overlay absolute left-0 top-full mt-2 max-h-[60vh] w-[360px]"
                >
                    <div className="rounded-lg border border-gray-300 bg-white shadow-lg">
                        <ul ref={listRef}>
                            {results.value
                                .slice(0, MAX_RESULTS)
                                .map((result, idx) => (
                                    <li
                                        key={idx}
                                        onClick={clear}
                                        className="mt-2 first:mt-0 break-words"
                                        data-list-index={idx}
                                    >
                                        {"link" in result.action
                                            ? getLinkResult(result)
                                            : getCallbackResult(result)}
                                    </li>
                                ))}
                        </ul>
                        {!query.value && (
                            <div>
                                <div className="flex justify-center fill-current py-4 text-gray-500">
                                    <SearchIcon width="24" height="24" />
                                    <span className="ml-2">Write a query</span>
                                </div>
                                {/* @TODO temporary hidden */}
                                <div className="hidden border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            issueTracker.show.value = true;
                                            isActive.value = false;
                                        }}
                                        className="flex items-center rounded-bl-lg p-3 text-gray-500 outline-none transition duration-100 hover:bg-gray-100 focus:text-sky-500"
                                    >
                                        <InventoryIcon
                                            width="18"
                                            height="18"
                                            className="fill-current"
                                        />
                                        <span className="ml-2 text-sm font-bold">
                                            Issue tracker
                                        </span>
                                    </button>
                                </div>
                            </div>
                        )}
                        {results.value.length === 0 && query.value && (
                            <div className="flex justify-center py-4 text-sm text-gray-500">
                                We found nothing related to your query
                            </div>
                        )}
                        {loading.value && (
                            <div className="flex justify-center py-2 text-sky-500">
                                <Loader width="16" height="16" />
                            </div>
                        )}
                    </div>
                </Transition>
            </div>
        </div>
    );
};

export default Search;
